import { Form, Formik, FormikErrors } from 'formik';

import { CustomerGroupSettingsMessage } from '../../../../../../types/CustomerGroupSettingsMessage';
import { Button } from '../../../../../cdl/Button/Button';
import { FormikInput } from '../../../../../cdl/Input/FormikInput';
import { Modal } from '../../../../../cdl/Modal/Modal';
import { Text } from '../../../../../cdl/Text/Text';
import { useToasts } from '../../../../../cdl/Toast/useToasts';
import { translate } from '../../../../../common/helpers/translate.helper';
import { Box } from '../../../../../common/ui/Box';
import { useCustomerGroupSettings } from '../../hooks/useCustomerGroupSettings';
import { useCustomerGroupSettingsUpdate } from '../../hooks/useCustomerGroupSettingsUpdate';

interface CustomerAddConnectionIdModalProps {
    isOpen: boolean;
    onDismiss: () => void;
    groupId: string;
    groupName: string;
}

interface FormValues {
    connectionId: string;
}

export const CustomerAddConnectionIdModal = ({
    isOpen,
    onDismiss,
    groupId,
    groupName,
}: CustomerAddConnectionIdModalProps) => {
    const settingsQuery = useCustomerGroupSettings(groupId);
    const updateMutation = useCustomerGroupSettingsUpdate(groupId);
    const { addToast, addErrorToast } = useToasts();

    const validate = (values: FormValues): FormikErrors<FormValues> => {
        const errors: FormikErrors<FormValues> = {};

        if (!values.connectionId || values.connectionId.trim().length === 0) {
            errors.connectionId = translate('sso.connectionIdRequired');
        }

        return errors;
    };

    const handleSubmit = async (values: FormValues) => {
        if (!settingsQuery.data) return;

        const updatedSettings: CustomerGroupSettingsMessage = {
            ...settingsQuery.data,
            ssoConfiguration: {
                connectionId: values.connectionId.trim(),
                samlGroupMapping: settingsQuery.data.ssoConfiguration?.samlGroupMapping || {},
            },
        };

        try {
            await updateMutation.mutateAsync({
                groupId,
                settings: updatedSettings,
            });
            addToast(translate('sso.enableSuccess', { groupName }));
            onDismiss();
        } catch {
            addErrorToast(translate('sso.enableError'));
        }
    };

    return (
        <Modal isOpen={isOpen} onDismiss={onDismiss}>
            <Box display="flex" flexDirection="column" gap={24} padding={24}>
                <Box display="flex" flexDirection="column" gap={8}>
                    <Text variant="subtitle1" as="h2">
                        {translate('sso.addConnectionIdModal.title')}
                    </Text>
                    <Text variant="body" color="foreground.subtle">
                        {translate('sso.addConnectionIdModal.description')}
                    </Text>
                </Box>

                <Formik
                    initialValues={{ connectionId: '' }}
                    validate={validate}
                    onSubmit={handleSubmit}
                    validateOnChange
                    validateOnBlur
                >
                    {({ isValid, dirty }) => (
                        <Form>
                            <Box display="flex" flexDirection="column" gap={24}>
                                <FormikInput
                                    name="connectionId"
                                    label={translate('sso.connectionIdLabel')}
                                    placeholder={translate('sso.connectionIdPlaceholder')}
                                    disabled={updateMutation.isPending}
                                />

                                <Box display="flex" justifyContent="flex-end" gap={8}>
                                    <Button type="button" emphasis="medium" onClick={onDismiss}>
                                        {translate('btn.cancel')}
                                    </Button>
                                    <Button
                                        type="submit"
                                        emphasis="high"
                                        disabled={!isValid || !dirty}
                                        isLoading={updateMutation.isPending}
                                    >
                                        {translate('sso.enableSsoButton')}
                                    </Button>
                                </Box>
                            </Box>
                        </Form>
                    )}
                </Formik>
            </Box>
        </Modal>
    );
};
